const REFRESH_LEAD_MS = 60_000;
const EAGER_REFRESH_WINDOW_MS = 120_000;
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "scroll"];

type RefreshHandler = () => Promise<boolean>;

let refreshHandler: RefreshHandler | null = null;
let timerId: number | null = null;
let expiresAt: number | null = null;
let hasInteracted = false;
let isListening = false;

const clearTimer = () => {
  if (timerId !== null) {
    window.clearTimeout(timerId);
    timerId = null;
  }
};

const evaluate = () => {
  if (expiresAt === null) {
    clearTimer();
    return;
  }

  if (document.visibilityState !== "visible") {
    clearTimer();
    return;
  }

  const dueIn = expiresAt - Date.now() - REFRESH_LEAD_MS;

  if (dueIn > 0) {
    clearTimer();
    timerId = window.setTimeout(evaluate, dueIn);
    return;
  }

  clearTimer();

  if (!hasInteracted) {
    return;
  }

  expiresAt = null;
  hasInteracted = false;

  void refreshHandler?.();
};

const onActivity = () => {
  hasInteracted = true;
  evaluate();
};

const startListening = () => {
  if (isListening) {
    return;
  }

  ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, onActivity, { passive: true }));
  document.addEventListener("visibilitychange", evaluate);
  isListening = true;
};

const stopListening = () => {
  if (!isListening) {
    return;
  }

  ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, onActivity));
  document.removeEventListener("visibilitychange", evaluate);
  isListening = false;
};

export const refreshScheduler = {
  setRefreshHandler: (handler: RefreshHandler | null) => {
    refreshHandler = handler;
  },

  arm: (expiresInSeconds: number) => {
    expiresAt = Date.now() + expiresInSeconds * 1000;
    hasInteracted = false;
    startListening();
    evaluate();
  },

  disarm: () => {
    clearTimer();
    stopListening();
    expiresAt = null;
    hasInteracted = false;
  },

  isExpiringSoon: () => expiresAt !== null && expiresAt - Date.now() <= EAGER_REFRESH_WINDOW_MS,
};
